/*
 * Copyright (c) Nexign, JSC, 2023.
 */
/**
 *
 *
 * @author Andrey Dubov
 * @author Alexander Golshtein
 * @version 1.24.0
 * @since 1.23.0
 */

import { Fragment, useCallback, useMemo, useRef, useState } from 'react';

import { MutationDefinition } from '@reduxjs/toolkit/dist/query';
import { MutationHooks } from '@reduxjs/toolkit/dist/query/react/buildHooks';
import { useFormikContext } from 'formik';
import debounce from 'lodash/debounce';
import { useSelector } from 'react-redux';

import { IInfiniteMutation, useInfiniteMutation } from '@/main/api/useInfiniteMutation';
import { Select } from '@components/common';
import { DropDownFilterButtonSize } from '@components/common/DropDownFilter/DropDownFilter';
import { FormLabelStyled } from '@components/common/FormStyled/FormStyled';
import { LabelMarkStyled } from '@components/common/Input/styled/InputStyled';
import { IOptionItemProps, ISelectProps, ISelectSearchProps } from '@components/common/Select/Select';
import { ITEMS_MIN_COUNT_FOR_SEARCH_VISIBILITY } from '@modules/form/const/formConstants';

import { ICreateInquiryAttributeDictionary } from '../../inquiry/types';
import { makeSelectTranslationCommon } from '../../internationalization/intSelectors';
import FormField, { IFormFieldProps } from './FormField';

type TFormFieldInfinite<Params extends Record<string, unknown>> = {
    endpoint: MutationHooks<
        MutationDefinition<IInfiniteMutation & Params, any, string, ICreateInquiryAttributeDictionary>
    >;
    params: Params;
};

type TFormFieldSelectInfinite<Params extends Record<string, unknown>> = Omit<IFormFieldProps, 'hintMessage'> &
    Partial<Pick<ISelectProps, 'onSelect' | 'maxWidth' | 'widthAuto'>> &
    TFormFieldInfinite<Params> & {
    withEmptyValue?: boolean;
};

export const FormFieldSelectInfinite = <Params extends Record<string, unknown>>({
    endpoint,
    params,
    widthAuto,
    maxWidth,
    onSelect,
    withEmptyValue = false,
    ...props
}: TFormFieldSelectInfinite<Params>) => {
    const translationCommon = useSelector(makeSelectTranslationCommon());

    const { setFieldTouched, setFieldValue, validateField } = useFormikContext();

    const { data, isLoading, fetchNextPage, hasMore, count } =
        useInfiniteMutation<IOptionItemProps, IInfiniteMutation & Params>(endpoint);

    const [searchString, setSearchString] = useState('');
    const refSearchString = useRef('');

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const delayedStartSearch = useCallback(
        debounce((searchValue: string) => {
            fetchNextPage({ searchValue, start: true, ...params });
        }, 500),
        [fetchNextPage],
    );

    const handleSearchChange = (searchValue: string) => {
        setSearchString(() => {
            refSearchString.current = searchValue;
            return searchValue;
        });

        delayedStartSearch(searchValue);
    };

    const onOpenAndClose = (isOpen: boolean) => {
        if (!isOpen) return;
        fetchNextPage({ searchValue: '', start: true, ...params });
    };

    const options = useMemo(() => {
        if (withEmptyValue) {
            const emptyPosition = { value: '', label: '' };
            return [emptyPosition, ...data];
        }
        return data;
    }, [data, withEmptyValue]);

    const virtualListProps = useMemo(
        () => ({
            isLoading: isLoading,
            hasMore,
            onScrollEvent: () => {
                fetchNextPage({ searchValue: refSearchString.current, start: false, ...params });
            },
            rowHeight: 32,
        }),

        [fetchNextPage, hasMore, isLoading, params],
    );

    const searchProps: ISelectSearchProps | undefined = count > ITEMS_MIN_COUNT_FOR_SEARCH_VISIBILITY
         ? {
            onSearchChange: handleSearchChange,
            searchValue: searchString,
        }
        : undefined

    return (
        <FormField {...props}>
            {({ field, kind, label, dataName, required, disabled, placeholder }) => {
                const onSelectHandler = (value: IOptionItemProps) => {
                    onSelect?.(value);
                    setFieldValue(field.name, value);
                    if (searchString) {
                        fetchNextPage({ searchValue: '', start: true, ...params });
                        setSearchString(() => {
                            refSearchString.current = '';
                            return '';
                        });
                    }
                    setTimeout(() => {
                        validateField(field.name);
                    });
                };

                return (
                    <Fragment>
                        {label && (
                            <FormLabelStyled>
                                {label}
                                {required && <LabelMarkStyled>*</LabelMarkStyled>}
                            </FormLabelStyled>
                        )}
                        <Select
                            dataName={dataName}
                            disabled={disabled}
                            filterButtonSize={DropDownFilterButtonSize.medium}
                            kind={kind}
                            maxWidth={maxWidth}
                            options={options}
                            placeholder={placeholder}
                            searchProps={searchProps}
                            selectedOption={field.value}
                            translation={translationCommon}
                            virtualListProps={virtualListProps}
                            widthAuto={widthAuto}
                            onCloseClick={() => setFieldTouched(field.name)}
                            onOpenAndClose={onOpenAndClose}
                            onSelect={onSelectHandler}
                        />
                    </Fragment>
                );
            }}
        </FormField>
    );
};
